<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const props = withDefaults(
  defineProps<{
    show: boolean
    title: string
    subtitle?: string
    confirmText?: string
    warningText?: string
    loading?: boolean
    deleteButtonText?: string
    deletingButtonText?: string
  }>(),
  {
    loading: false
  }
)

const emit = defineEmits<{
  (e: 'delete'): void
  (e: 'close'): void
}>()

const { t } = useI18n()

const showDialog = ref(false)

const deleteLabel = computed(() =>
  props.loading
    ? (props.deletingButtonText || t('common.deleting'))
    : (props.deleteButtonText || t('common.delete'))
)

const handleClose = () => {
  showDialog.value = false
  emit('close')
}

const handleDelete = () => {
  emit('delete')
}

watch(
  () => props.show,
  (newVal) => {
    showDialog.value = newVal
  },
  { immediate: true }
)

watch(showDialog, (newVal) => {
  if (!newVal) {
    emit('close')
  }
})
</script>

<template>
  <v-dialog v-model="showDialog" max-width="480" persistent>
    <div class="delete-modal">
      <!-- Header -->
      <div class="modal-header">
        <div class="header-icon">
          <v-icon icon="mdi-trash-can-outline" size="24" />
        </div>
        <div class="header-text">
          <h2 class="modal-title">{{ title }}</h2>
          <p v-if="subtitle" class="modal-subtitle">{{ subtitle }}</p>
        </div>
        <button class="close-btn" @click="handleClose">
          <v-icon icon="mdi-close" size="22" />
        </button>
      </div>

      <!-- Content -->
      <div class="modal-content">
        <p v-if="confirmText" class="confirm-text">{{ confirmText }}</p>

        <div v-if="$slots.preview" class="preview-box">
          <slot name="preview" />
        </div>

        <div v-if="warningText" class="warning-note">
          <v-icon icon="mdi-alert-circle-outline" size="18" />
          <span>{{ warningText }}</span>
        </div>
      </div>

      <!-- Footer -->
      <div class="modal-footer">
        <button class="btn secondary" :disabled="loading" @click="handleClose">
          {{ $t('common.cancel') }}
        </button>
        <button class="btn danger" :disabled="loading" @click="handleDelete">
          <v-progress-circular v-if="loading" indeterminate size="18" width="2" />
          <v-icon v-else icon="mdi-trash-can" size="18" />
          {{ deleteLabel }}
        </button>
      </div>
    </div>
  </v-dialog>
</template>

<style lang="scss" scoped>
.delete-modal {
  background: rgba(15, 23, 48, 0.98);
  border: 1px solid rgba(244, 67, 54, 0.2);
  border-radius: 16px;
  overflow: hidden;
}

.modal-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 20px 24px;
  border-bottom: 1px solid rgba(120, 163, 255, 0.1);
}

.header-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: rgba(244, 67, 54, 0.12);
  border: 1px solid rgba(244, 67, 54, 0.25);
  border-radius: 12px;
  color: #ef5350;
}

.header-text {
  flex: 1;
}

.modal-title {
  font-size: 1.15rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  margin: 0 0 2px;
}

.modal-subtitle {
  font-size: 0.85rem;
  color: rgba(244, 67, 54, 0.8);
  margin: 0;
}

.close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: rgba(200, 218, 255, 0.5);
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(120, 163, 255, 0.1);
    color: rgba(200, 218, 255, 0.9);
  }
}

.modal-content {
  padding: 24px;
}

.confirm-text {
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.85);
  margin: 0 0 16px;
}

.preview-box {
  background: rgba(120, 163, 255, 0.04);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
}

.warning-note {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  background: rgba(255, 152, 0, 0.08);
  border: 1px solid rgba(255, 152, 0, 0.15);
  border-radius: 10px;
  color: rgba(255, 193, 7, 0.9);
  font-size: 0.85rem;
  line-height: 1.5;

  .v-icon {
    flex-shrink: 0;
    margin-top: 1px;
  }
}

.modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px;
  border-top: 1px solid rgba(120, 163, 255, 0.1);
}

.btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &.secondary {
    background: rgba(120, 163, 255, 0.08);
    border: 1px solid rgba(120, 163, 255, 0.15);
    color: rgba(200, 218, 255, 0.8);

    &:hover:not(:disabled) {
      background: rgba(120, 163, 255, 0.15);
    }
  }

  &.danger {
    background: rgba(244, 67, 54, 0.15);
    border: 1px solid rgba(244, 67, 54, 0.3);
    color: #ef5350;

    &:hover:not(:disabled) {
      background: rgba(244, 67, 54, 0.25);
      border-color: rgba(244, 67, 54, 0.45);
    }
  }
}

/* Light Theme */
.v-theme--lightTheme {
  .delete-modal {
    background: rgba(255, 255, 255, 0.98);
    border-color: rgba(244, 67, 54, 0.2);
  }

  .modal-title {
    color: rgba(15, 23, 42, 0.95);
  }

  .confirm-text {
    color: rgba(15, 23, 42, 0.85);
  }
}
</style>

<style lang="scss">
/* Unscoped styles for slotted content within BaseDeleteModal */
.delete-modal .preview-box {
  .preview-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 0;

    &:not(:last-child) {
      border-bottom: 1px solid rgba(120, 163, 255, 0.06);
    }

    &:first-child {
      padding-top: 0;
    }

    &:last-child {
      padding-bottom: 0;
    }
  }

  .preview-label {
    font-size: 0.85rem;
    color: rgba(200, 218, 255, 0.5);
  }

  .preview-content {
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.85);
    max-width: 280px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-align: right;
  }

  code {
    background: rgba(120, 163, 255, 0.1);
    padding: 4px 8px;
    border-radius: 6px;
    font-size: 0.85rem;
    font-family: 'JetBrains Mono', 'Fira Code', monospace;
    color: rgba(200, 218, 255, 0.9);
  }
}

.v-theme--lightTheme .delete-modal .preview-box {
  .preview-content {
    color: rgba(15, 23, 42, 0.85);
  }

  code {
    background: rgba(28, 110, 227, 0.08);
    color: rgba(15, 23, 42, 0.9);
  }
}
</style>
